import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import Footer from '../components/sections/Footer';
import SEO from '../components/SEO';

const AgentPartnership = () => {
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh';

  const t = (en, zh) => (isZh ? zh : en);

  return (
    <div className="min-h-screen bg-white" data-testid="agent-partnership-page">
      <SEO
        title={t('ALIGCUS Agent Partnership Program — ADU Development in California', 'ALIGCUS 地产经纪合作计划 — 加州 ADU 开发')}
        description={t(
          'Unlock new real estate opportunities. ALIGCUS offers a comprehensive, no-cost collaboration model for real estate professionals building ADUs across California.',
          '解锁全新地产机会。ALIGCUS 为加州地产经纪人提供全面免费的 ADU 合作模式，创造互利共赢。'
        )}
      />

      {/* ============ HERO / Slide 1 ============ */}
      <section className="relative bg-gradient-to-br from-red-600 via-red-700 to-red-900 text-white py-24 sm:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-96 h-96 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-yellow-300 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-6xl mx-auto px-6 text-center">
          <p className="uppercase tracking-widest text-sm text-yellow-200 font-semibold mb-4" data-testid="hero-tagline">
            {t('Unlock New Real Estate Opportunities', '解锁全新地产机会')}
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight" data-testid="hero-title">
            {t('ADU Development in California', '加州 ADU 开发')}
          </h1>
          <p className="text-xl sm:text-2xl mb-4 text-white/90">
            {t('Policies · Types · Case Studies', '政策 · 类型 · 案例分析')}
          </p>
          <p className="max-w-3xl mx-auto text-base sm:text-lg text-white/80 mb-10">
            {t(
              'ALIGCUS offers a comprehensive, no-cost collaboration model designed for today\'s real estate market, creating mutual value for real estate professionals.',
              'ALIGCUS 为当今地产市场量身打造完整的免费合作模式，为地产从业者创造互利共赢的价值。'
            )}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:408-900-6688" data-testid="hero-cta-call">
              <Button className="bg-white text-red-700 hover:bg-yellow-100 font-bold px-8 py-6 text-lg">
                📞 408-900-6688
              </Button>
            </a>
            <Link to={isZh ? '/zh/estimate' : '/estimate'} data-testid="hero-cta-quote">
              <Button variant="outline" className="border-2 border-white text-white bg-transparent hover:bg-white hover:text-red-700 font-bold px-8 py-6 text-lg">
                {t('Free Consultation', '免费咨询')}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ============ WHAT IS ADU / Slide 2 ============ */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-red-600 font-semibold uppercase tracking-wider mb-2 text-sm">
                {t('Accessory Dwelling Unit', '附属住宅单元')}
              </p>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                {t('What is an ADU?', '什么是 ADU？')}
              </h2>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                {t(
                  'An ADU is a fully independent, self-contained living unit with all necessary amenities.',
                  'ADU 是拥有全部生活设施的独立自治住宅单元。'
                )}
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                {t(
                  'An ADU features a full kitchen, bedroom, and bathroom, and may also have separate utility meters and its own street address.',
                  'ADU 拥有独立厨房、卧室和卫浴，还可以有独立的水电表和门牌地址。'
                )}
              </p>
            </div>
            <div className="bg-white rounded-3xl shadow-xl p-8 border-l-8 border-red-600">
              <div className="text-8xl font-bold text-red-600 mb-2">ADU</div>
              <div className="space-y-2 text-gray-700">
                <p><strong className="text-red-700">A</strong>ccessory</p>
                <p><strong className="text-red-700">D</strong>welling</p>
                <p><strong className="text-red-700">U</strong>nit</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ HOUSING CRISIS / Slide 3 ============ */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-6xl mx-auto px-6">
          <p className="text-yellow-400 uppercase tracking-widest text-sm font-semibold mb-2 text-center">
            {t('2026 Housing Crisis', '2026 住房危机')}
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12">
            {t('U.S. Housing Shortage Grows to 4.03 Million Units', '美国住房缺口攀升至 403 万套')}
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gray-800 rounded-2xl p-8">
              <div className="text-6xl font-bold text-red-500 mb-3">4.03M</div>
              <p className="text-lg mb-3">
                {t('U.S. Housing Deficit', '美国住房缺口')}
              </p>
              <p className="text-sm text-gray-400 leading-relaxed">
                {t(
                  'Realtor.com\'s 2026 Housing Supply Gap Report shows the U.S. housing deficit rose from 3.8 million units in 2024 to an estimated 4.03 million units in 2025.',
                  'Realtor.com 2026 供应缺口报告显示，美国住房缺口从 2024 年的 380 万套上升至 2025 年估计的 403 万套。'
                )}
              </p>
              <p className="text-xs text-gray-500 mt-4 italic">
                {t('According to a Reuters report dated March 3, 2026', '数据来源：路透社 2026 年 3 月 3 日报道')}
              </p>
            </div>
            <div className="bg-gray-800 rounded-2xl p-8 border-2 border-red-600">
              <div className="text-6xl font-bold text-red-500 mb-3">1M+</div>
              <p className="text-lg mb-3">
                {t('Housing Shortage', '住房缺口')}
              </p>
              <ul className="text-sm text-gray-300 space-y-2">
                <li>• {t('20% of California\'s population, but 40–50% of the housing shortage', '仅占加州 20% 人口，却占 40–50% 住房缺口')}</li>
                <li>• {t('One of the most severe housing shortages in the U.S.', '全美最严重的住房缺口地区之一')}</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ============ CALIFORNIA ADU EVOLUTION / Slide 4 ============ */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
              {t('The Evolution of California ADU Law', '加州 ADU 法规演变')}
            </h2>
            <p className="text-gray-600 text-lg">
              {t('Key milestones from pre-2016 to 2026', '2016 之前 到 2026 的关键里程碑')}
            </p>
          </div>

          <div className="space-y-6">
            {[
              {
                year: t('Pre 2016', '2016 之前'),
                title: t('ADU "Legal but Blocked"', 'ADU「合法但受限」'),
                items: [t('Many restrictions', '诸多限制'), t('Required public hearings', '需公开听证会')],
              },
              {
                year: '2016-17',
                title: t('Turning Point', '转折点'),
                items: [
                  t('Clarified ADU can be built on single-family lot', '明确 ADU 可建于独栋住宅地块'),
                  t('No more public hearing', '不再需要公开听证'),
                  t('Reduced parking requirements', '降低停车位要求'),
                ],
              },
              {
                year: '2019-20',
                title: t('"State Override" Era', '「州级凌驾」时代'),
                items: [
                  t('60-day permit approval requirement', '60 天许可审批要求'),
                  t('Eliminate local barriers (parking, owner occupancy)', '消除地方壁垒（停车、业主自住等）'),
                  t('Allow multiple ADUs per lot + JADU', '允许每个地块多个 ADU + Junior ADU'),
                ],
              },
              {
                year: '2021-22',
                title: t('State Standardization & Scaling', '州级标准化与推广'),
                items: [
                  t('Must allow up to 800 sqft ADU regardless of zoning', '不论分区都必须允许 800 平方英尺 ADU'),
                  t('Setbacks tightened to 4 ft', '退线缩至 4 英尺'),
                ],
              },
              {
                year: '2023-24',
                title: t('Ownership Change', '所有权变革'),
                items: [
                  t('AB1033: ADU can be sold separately as condos', 'AB1033：ADU 可作为公寓单独出售'),
                  t('Continued legal cleanup and code restructuring', '持续法律清理与法规重构'),
                ],
              },
              {
                year: '2025',
                title: t('Acceleration & Expansion', '加速与扩张'),
                items: [
                  t('Pre-approved ADU plans required statewide', '全州要求预审批 ADU 图纸'),
                  t('Multifamily properties may allow up to 8 detached ADUs', '多户住宅可允许多达 8 栋独立 ADU'),
                ],
                highlight: true,
              },
              {
                year: '2026',
                title: t('Enforcement & Process Guarantees', '执行与流程保障'),
                items: [
                  t('15-day completeness review deadline for applications', '15 天完整性审查截止期'),
                  t('Fines for cities that block ADUs', '阻碍 ADU 的城市将被罚款'),
                  t('Faster court reviews of denials', '拒批的加速司法审查'),
                  t('ADUs < 500 sqft exempt from some school fees', '500 平方英尺以下 ADU 免部分学区费'),
                ],
                highlight: true,
              },
            ].map((era, idx) => (
              <div
                key={idx}
                className={`flex flex-col md:flex-row gap-4 items-start p-6 rounded-xl border-l-4 ${
                  era.highlight ? 'border-red-600 bg-red-50' : 'border-gray-300 bg-gray-50'
                }`}
                data-testid={`adu-era-${idx}`}
              >
                <div className={`md:w-40 flex-shrink-0 ${era.highlight ? 'text-red-700' : 'text-gray-800'}`}>
                  <div className="text-2xl font-bold">{era.year}</div>
                </div>
                <div className="flex-1">
                  <h3 className={`text-xl font-bold mb-2 ${era.highlight ? 'text-red-700' : 'text-gray-900'}`}>
                    {era.title}
                  </h3>
                  <ul className="space-y-1 text-gray-700">
                    {era.items.map((item, i) => (
                      <li key={i}>• {item}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 text-center bg-red-600 text-white py-4 px-6 rounded-xl">
            <p className="text-lg font-semibold">
              {t('CALL: 408-900-6688 for appointment!', '预约请拨打：408-900-6688')}
            </p>
          </div>
        </div>
      </section>

      {/* ============ CITIES ALLOW CONDO SALES / Slide 5 ============ */}
      <section className="py-20 bg-gradient-to-br from-yellow-50 to-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-red-600 uppercase tracking-widest text-sm font-semibold mb-2">
              {t('2026 Update', '2026 最新')}
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              {t('California Cities Now Allow ADU Condo Sales', '加州多座城市已允许 ADU 公寓化销售')}
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { region: t('Bay Area', '湾区'), cities: 'San José, Santa Cruz, Berkeley' },
              { region: t('Los Angeles', '洛杉矶'), cities: 'Culver City, Santa Monica' },
              { region: t('San Diego', '圣地亚哥'), cities: 'San Diego County, City of San Diego' },
              { region: t('Coming Soon', '即将扩展'), cities: t('Additional cities in review', '更多城市评审中') },
            ].map((c, i) => (
              <div
                key={i}
                className="bg-white rounded-xl p-6 shadow-md border-t-4 border-red-600 hover:shadow-xl transition-shadow"
                data-testid={`condo-city-${i}`}
              >
                <h3 className="font-bold text-red-700 text-lg mb-3">{c.region}</h3>
                <p className="text-gray-700 text-sm">{c.cities}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ ADU TYPES / Slides 6-7 ============ */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
              {t('ADU Types', 'ADU 类型')}
            </h2>
            <p className="text-gray-600 text-lg">
              {t('By relationship with main house', '按与主住宅的关系分类')}
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                title: t('Detached ADU', '独立 ADU'),
                size: t('Up to 1,200 sqft', '最大 1,200 平方英尺'),
                desc: t(
                  'Detached from primary dwelling; newly-constructed or converted from existing accessory structure.',
                  '与主住宅分离；新建或由现有附属结构改建。'
                ),
                icon: '🏠',
              },
              {
                title: t('Attached ADU', '附属 ADU'),
                size: t('Up to 1,200 sqft (max 50% of primary)', '最大 1,200 平方英尺（不超主宅 50%）'),
                desc: t(
                  'Created from addition, internal conversion, or combination to primary dwelling.',
                  '通过加建、内部改造或结合的方式与主住宅一体化。'
                ),
                icon: '🏘️',
              },
              {
                title: t('Junior ADU (JADU)', '初级 ADU (JADU)'),
                size: t('Up to 500 sqft', '最大 500 平方英尺'),
                desc: t(
                  'Created by conversion of existing space within the walls of a single-family residence.',
                  '在独栋住宅现有墙体范围内改造出来的居住单元。'
                ),
                icon: '🏡',
              },
            ].map((type, i) => (
              <div
                key={i}
                className="bg-gradient-to-br from-red-50 to-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all border border-red-100"
                data-testid={`adu-type-${i}`}
              >
                <div className="text-5xl mb-4">{type.icon}</div>
                <h3 className="text-xl font-bold text-red-700 mb-2">{type.title}</h3>
                <p className="text-sm font-semibold text-gray-600 mb-4">{type.size}</p>
                <p className="text-gray-700 leading-relaxed">{type.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SETBACK / Slide 8 ============ */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3 text-center">
            {t('Setback Requirements', '退线要求')}
          </h2>
          <p className="text-gray-600 text-center mb-10">
            {t('Setback defined more by fire/building code, not zoning', '退线更多由消防/建筑规范决定，而非分区')}
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              t('The requirement is to reduce the setback for all newly constructed ADU in rear and side yards to 4 feet', '所有新建 ADU 的后院和侧院退线要求已降至 4 英尺'),
              t('Converting an existing garage or structure into ADU without changing footprint is allowed; setback can be less than 4 feet', '将现有车库或结构改建为 ADU 且不改变占地面积时，退线可少于 4 英尺'),
              t('Easement requires additional setbacks', '地役权要求额外退线'),
              t('Setback from primary dwelling typically min. 6 ft', '与主住宅的退线通常最少 6 英尺'),
            ].map((item, i) => (
              <div key={i} className="bg-white p-6 rounded-xl shadow-sm flex items-start gap-4" data-testid={`setback-${i}`}>
                <div className="w-10 h-10 rounded-full bg-red-600 text-white flex items-center justify-center font-bold flex-shrink-0">
                  {i + 1}
                </div>
                <p className="text-gray-700 leading-relaxed">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ ADU ADVANTAGES / Slide 9 ============ */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3 text-center">
            {t('ADU Advantages', 'ADU 优势')}
          </h2>
          <p className="text-gray-600 text-center mb-12 text-lg">
            {t('ADUs are powerful, but not perfect', 'ADU 优势显著，但也非完美')}
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              t('Strong rental income potential', '强劲租金收入潜力'),
              t('Significant property value increase', '显著提升物业价值'),
              t('Multigenerational living flexibility', '多代同住的灵活性'),
              t('Flexible use over time', '长期使用的灵活性'),
              t('Easier to build than primary house', '比主住宅更易建造'),
              t('Lower cost than buying another property', '成本低于购买另一处物业'),
              t('Efficient land use', '高效土地利用'),
              t('Tax and financial advantages (situational)', '税务与财务优势（视情况）'),
              t('Faster construction timeline', '更快的建造周期'),
              t('Lower regulatory risk (in California)', '较低的监管风险（在加州）'),
            ].map((adv, i) => (
              <div
                key={i}
                className="bg-gradient-to-br from-red-600 to-red-700 text-white p-4 rounded-xl text-center shadow-md hover:scale-105 transition-transform"
                data-testid={`advantage-${i}`}
              >
                <div className="text-2xl font-bold mb-2">{i + 1}</div>
                <p className="text-sm leading-tight">{adv}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 max-w-2xl mx-auto bg-yellow-50 border-l-4 border-yellow-500 p-6 rounded-lg">
            <p className="text-gray-700 font-semibold mb-1">
              {t('Considerations:', '需考量：')}
            </p>
            <ul className="text-gray-700 space-y-1">
              <li>• {t('Upfront cost', '前期投入成本')}</li>
              <li>• {t('Landlord responsibilities', '房东责任')}</li>
            </ul>
          </div>
        </div>
      </section>

      {/* ============ ABOUT ALIGCUS / Slide 10 ============ */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-yellow-400 uppercase tracking-widest text-sm font-semibold mb-2">
              {t('Leading ADU Builder', '领先的 ADU 建筑商')}
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              {t('ALIGCUS Was Founded in 2008', 'ALIGCUS 成立于 2008 年')}
            </h2>
            <p className="text-gray-300 max-w-3xl mx-auto text-lg leading-relaxed">
              {t(
                'Almost two decades of development, we provide the market with practical construction management strategies. Turnkey Solution: Design & Construction.',
                '近二十年的发展，我们为市场提供切实可行的建筑管理策略。设计 + 施工一站式解决方案。'
              )}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {[
              { num: '1,500+', label: t('ADUs Built in California', '加州已建成 ADU') },
              { num: '10+', label: t('Years Structural Warranty', '年结构质保') },
              { num: '2008', label: t('Founded', '成立年份') },
              { num: t('Free', '免费'), label: t('Consultation & Quote', '咨询与报价') },
            ].map((stat, i) => (
              <div key={i} className="bg-red-700 rounded-xl p-6 text-center" data-testid={`aligcus-stat-${i}`}>
                <div className="text-4xl font-bold mb-2">{stat.num}</div>
                <div className="text-sm text-white/90">{stat.label}</div>
              </div>
            ))}
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[
              t('Traditional builder (not prefab ADU), guaranteed quality', '传统建筑（非预制 ADU），品质有保障'),
              t('Quality guaranteed, worry-free after-sales services', '品质保证 + 无忧售后服务'),
              t('In-house architectural design team', '内部专业建筑设计团队'),
              t('Building consultant on standby even after sales', '售后仍有建筑顾问随时待命'),
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3 bg-gray-800 p-4 rounded-lg">
                <span className="text-yellow-400 text-2xl flex-shrink-0">✓</span>
                <p>{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ PRE-APPROVAL NETWORK / Slide 11 ============ */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-red-600 uppercase tracking-widest text-sm font-semibold mb-2">
              {t('VIP Fast Track', 'VIP 快速通道')}
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
              {t('ALIGCUS Pre-Approval Program', 'ALIGCUS 预审批计划')}
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              {t(
                'Approved in Multiple California Cities — Saving You Up to 5 Months! Under California\'s AB 1332, cities must establish ADU pre-approved plan programs.',
                '已在加州多座城市获得预审批 — 为您节省长达 5 个月的时间！根据加州 AB 1332 法案，城市必须建立 ADU 预审批图纸计划。'
              )}
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 mb-10">
            <div className="bg-gradient-to-br from-red-50 to-white rounded-2xl p-8 border-l-8 border-red-600">
              <h3 className="text-2xl font-bold text-red-700 mb-4">
                {t('Southern California', '南加州')}
              </h3>
              <div className="mb-3">
                <p className="font-semibold text-gray-800 mb-2">
                  {t('Approved:', '已通过：')}
                </p>
                <p className="text-gray-700">Rosemead · Temple City · Azusa · Los Angeles County · Monterey Park</p>
              </div>
              <div>
                <p className="font-semibold text-gray-800 mb-2">
                  {t('Coming Soon:', '即将推出：')}
                </p>
                <p className="text-gray-700">West Covina · Walnut · Alhambra · {t('and more', '及更多')}</p>
              </div>
            </div>
            <div className="bg-gradient-to-br from-red-50 to-white rounded-2xl p-8 border-l-8 border-red-600">
              <h3 className="text-2xl font-bold text-red-700 mb-4">
                {t('Northern California', '北加州')}
              </h3>
              <div className="mb-3">
                <p className="font-semibold text-gray-800 mb-2">
                  {t('Approved:', '已通过：')}
                </p>
                <p className="text-gray-700">San Jose · Fremont</p>
              </div>
              <div>
                <p className="font-semibold text-gray-800 mb-2">
                  {t('Coming Soon:', '即将推出：')}
                </p>
                <p className="text-gray-700">Milpitas · {t('and more', '及更多')}</p>
              </div>
            </div>
          </div>
          <div className="text-center bg-red-600 text-white py-6 px-8 rounded-2xl">
            <p className="text-2xl font-bold mb-2">
              {t('Build ADU Without Delays', '建造 ADU 无需等待')}
            </p>
            <p className="text-lg text-white/90">
              {t('Sign Today, Break Ground Tomorrow · 800–1,200 Sq Ft · Multiple Floor Plans Available', '今日签约，明日动工 · 800–1,200 平方英尺 · 多种户型可选')}
            </p>
          </div>
        </div>
      </section>

      {/* ============ SERVICE PROCESS / Slide 12 ============ */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-900 mb-3">
            {t('ALIGCUS Service Process', 'ALIGCUS 服务流程')}
          </h2>
          <p className="text-center text-gray-600 mb-12 text-lg">
            {t('9 installment payments upon milestones', '按里程碑分 9 期付款')}
          </p>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              {
                num: '01',
                title: t('Inquiry & Contract', '咨询与合同'),
                items: [
                  t('Free consulting project plan', '免费咨询项目方案'),
                  t('Reaching project agreement', '达成项目协议'),
                  t('Signing ceremony', '签约仪式'),
                ],
              },
              {
                num: '02',
                title: t('Plan Approval', '图纸审批'),
                items: [
                  t('Construction design', '施工设计'),
                  t('Submittal to cities', '递交城市部门'),
                  t('Receive approvals', '获得审批'),
                ],
              },
              {
                num: '03',
                title: t('Construction', '施工建造'),
                items: [
                  t('Structure construction', '结构施工'),
                  t('Interior construction', '内部装修'),
                  t('Appliances installation', '电器安装'),
                ],
              },
              {
                num: '04',
                title: t('Completion & Inspection', '竣工与验收'),
                items: [
                  t('Project inspection', '项目检查'),
                  t('Project acceptance', '项目验收'),
                  t('Completion & delivery', '竣工交付'),
                ],
              },
            ].map((step, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 shadow-lg" data-testid={`process-step-${i}`}>
                <div className="text-5xl font-bold text-red-600 mb-3">{step.num}</div>
                <h3 className="text-lg font-bold text-gray-900 mb-4">{step.title}</h3>
                <ul className="space-y-2">
                  {step.items.map((it, j) => (
                    <li key={j} className="text-sm text-gray-600 flex items-start">
                      <span className="text-red-600 mr-2">•</span>
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ TIMELINE / Slides 13-15 ============ */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-900 mb-12">
            {t('Project Timeline & Cost', '项目周期与成本')}
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-red-600 to-red-700 text-white rounded-2xl p-8 shadow-xl">
              <p className="text-sm uppercase tracking-widest opacity-80 mb-2">
                {t('Permit Process', '许可流程')}
              </p>
              <h3 className="text-2xl font-bold mb-4">
                {t('City / County', '城市 / 郡')}
              </h3>
              <ul className="space-y-2">
                <li>• {t('City: 3-6 months', '城市：3–6 个月')}</li>
                <li>• {t('County: 5-8 months', '郡：5–8 个月')}</li>
              </ul>
            </div>
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 text-white rounded-2xl p-8 shadow-xl">
              <p className="text-sm uppercase tracking-widest opacity-80 mb-2">
                {t('Construction', '施工')}
              </p>
              <h3 className="text-2xl font-bold mb-4">
                {t('Time Frame', '施工周期')}
              </h3>
              <ul className="space-y-2">
                <li>• {t('One story: 4-6 months', '单层：4–6 个月')}</li>
                <li>• {t('Two stories: 5-8 months', '双层：5–8 个月')}</li>
              </ul>
            </div>
            <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 text-white rounded-2xl p-8 shadow-xl">
              <p className="text-sm uppercase tracking-widest opacity-80 mb-2">
                {t('Cost Components', '成本组成')}
              </p>
              <h3 className="text-2xl font-bold mb-4">
                {t('Transparent', '透明费用')}
              </h3>
              <ul className="space-y-2 text-sm">
                <li>• {t('Design & Construction', '设计与施工')}</li>
                <li>• {t('Government fees', '政府费用')}</li>
                <li>• {t('3rd party fees (survey, PG&E, etc.)', '第三方费用（勘察、PG&E 等）')}</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ============ CASE STUDIES / Slides 17-19 ============ */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <p className="text-red-600 uppercase tracking-widest text-sm font-semibold mb-2 text-center">
            {t('Latest Product Cases', '最新产品案例')}
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-900 mb-12">
            {t('Model Home Opening', '样板房开放')}
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl overflow-hidden shadow-xl" data-testid="case-adu">
              <div className="bg-gradient-to-br from-red-600 to-red-800 text-white p-8">
                <p className="text-yellow-300 text-sm uppercase tracking-widest mb-2">
                  {t('New Generation 4.0 Premium ADU', '新一代 4.0 高端 ADU')}
                </p>
                <h3 className="text-2xl font-bold mb-2">
                  {t('Italian Luxury Living ADU', '意式奢华生活 ADU')}
                </h3>
                <p className="text-white/80">
                  {t('Compact Footprint · Estate-style Living', '紧凑占地 · 庄园级生活')}
                </p>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-3 gap-4 mb-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-red-700">1,000</div>
                    <div className="text-xs text-gray-500">sqft</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-red-700">3</div>
                    <div className="text-xs text-gray-500">{t('Beds', '卧室')}</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-red-700">2</div>
                    <div className="text-xs text-gray-500">{t('Baths', '浴室')}</div>
                  </div>
                </div>
                <p className="text-sm text-gray-600 border-t pt-3">
                  📍 1856 California St, San Gabriel, CA 91776
                </p>
              </div>
            </div>
            <div className="bg-white rounded-2xl overflow-hidden shadow-xl" data-testid="case-main">
              <div className="bg-gradient-to-br from-gray-800 to-gray-900 text-white p-8">
                <p className="text-yellow-300 text-sm uppercase tracking-widest mb-2">
                  {t('Whole-House Renovation', '全屋翻新')}
                </p>
                <h3 className="text-2xl font-bold mb-2">
                  {t('Italian Luxury Main House', '意式奢华主屋')}
                </h3>
                <p className="text-white/80">
                  {t('Redesigned Layout · Doubled Space', '重新设计布局 · 空间翻倍')}
                </p>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-3 gap-4 mb-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-gray-800">1,187</div>
                    <div className="text-xs text-gray-500">sqft</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-800">3</div>
                    <div className="text-xs text-gray-500">{t('Beds', '卧室')}</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-800">2</div>
                    <div className="text-xs text-gray-500">{t('Baths', '浴室')}</div>
                  </div>
                </div>
                <p className="text-sm text-gray-600 border-t pt-3">
                  📍 1856 California St, San Gabriel, CA 91776
                </p>
              </div>
            </div>
          </div>
          <div className="mt-8 text-center">
            <Link to={isZh ? '/zh/portfolio' : '/portfolio'} data-testid="view-portfolio-cta">
              <Button className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg">
                {t('View All Portfolio', '查看完整案例')} →
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ============ FREE SERVICES / Slide 20 ============ */}
      <section className="py-20 bg-red-600 text-white">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <p className="uppercase tracking-widest text-sm text-yellow-300 font-semibold mb-3">
            {t('What\'s In It For Clients', '客户可获得什么')}
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold mb-12">
            {t('Everything Below Is 100% FREE', '以下服务全部 100% 免费')}
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
            {[
              t('On-site Consultation', '现场咨询'),
              t('On-site Measurements', '现场测量'),
              t('Customized Floor & Site Plan', '定制户型与场地图'),
              t('Detailed Quotation', '详细报价'),
            ].map((item, i) => (
              <div key={i} className="bg-white/10 backdrop-blur rounded-2xl p-6 border border-white/20" data-testid={`free-service-${i}`}>
                <div className="text-4xl font-bold text-yellow-300 mb-2">0{i + 1}</div>
                <p className="text-lg font-semibold">{item}</p>
              </div>
            ))}
          </div>
          <div className="text-2xl sm:text-3xl font-bold">
            📞 <a href="tel:408-900-6688" className="underline hover:text-yellow-300 transition-colors">408-900-6688</a>
          </div>
          <p className="mt-2 text-white/80">
            {t('Unlock the Value — Call for Appointment!', '解锁全部价值 — 立即致电预约！')}
          </p>
        </div>
      </section>

      {/* ============ PARTNERSHIP FOR AGENTS / Slides 21-23 ============ */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-red-600 uppercase tracking-widest text-sm font-semibold mb-2">
              {t('For Real Estate Agents', '致地产经纪人')}
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              {t('Free & Comprehensive Resource Sharing', '免费且全面的资源共享')}
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto text-lg leading-relaxed">
              {t(
                'ALIGCUS possesses extensive real estate resources available for collaborative sharing. We offer generous referral fees to referring agents.',
                'ALIGCUS 拥有丰富的地产资源可供合作共享。我们为推荐经纪人提供丰厚的推荐佣金。'
              )}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-gradient-to-br from-red-50 to-white rounded-2xl p-8 border-t-4 border-red-600" data-testid="partnership-01">
              <div className="text-red-600 font-bold text-3xl mb-3">01</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                {t('Well-funded Real Estate Investors', '资金雄厚的地产投资人')}
              </h3>
              <p className="text-gray-700 leading-relaxed">
                {t(
                  'Strong interest in apartment, commercial, and ADU-eligible residential lands. We can directly connect you with investment-oriented buyers for win-win outcomes.',
                  '对公寓、商业与可建 ADU 的住宅土地表现出浓厚兴趣。我们可直接对接投资型买家，实现共赢。'
                )}
              </p>
            </div>
            <div className="bg-gradient-to-br from-red-50 to-white rounded-2xl p-8 border-t-4 border-red-600" data-testid="partnership-02">
              <div className="text-red-600 font-bold text-3xl mb-3">02</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                {t('Free Real Estate Seminars', '免费地产研讨会')}
              </h3>
              <p className="text-gray-700 leading-relaxed">
                {t(
                  'ALIGCUS regularly hosts free and innovative real estate seminars at our office. An exclusive social network for real estate agents — you are cordially invited to join!',
                  'ALIGCUS 定期在办公室举办免费且创新的地产研讨会。这是地产经纪人的专属社交网络 — 诚挚邀请您加入！'
                )}
              </p>
            </div>
          </div>

          <div className="bg-gray-900 text-white rounded-3xl p-8 sm:p-12">
            <h3 className="text-2xl sm:text-3xl font-bold text-center mb-8">
              {t('What Agents Get With ALIGCUS', '经纪人与 ALIGCUS 合作可获得')}
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                t('Free ADU Addition Proposals', '免费 ADU 加建方案'),
                t('Free Architectural Consultation', '免费建筑咨询'),
                t('Free Marketing & Collateral Material', '免费营销与宣传物料'),
                t('Free Professional Architectural Consulting', '免费专业建筑咨询'),
                t('On-site Support During Open Houses', 'Open House 现场支持'),
                t('Comprehensive Resource Sharing', '全面的资源共享'),
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 bg-white/10 backdrop-blur p-4 rounded-xl" data-testid={`agent-benefit-${i}`}>
                  <span className="text-yellow-400 text-xl flex-shrink-0">✓</span>
                  <p className="text-sm sm:text-base">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AgentPartnership;
